import React, { useCallback, useEffect, useMemo, useState } from "react";
import InlineGroup from "../../atoms/inlinegroup/InlineGroup";
import VerticalGroup from "../../atoms/verticalgroup/VerticalGroup";
import Text from "../../atoms/text/Text";
import { getMaxClicks as getBankBailOutsMaxClicks } from "./slides/RoundResultsBankBailOuts";
import { maxClicks as marketMargingMaxClicks } from "./slides/RoundResultsMarketMargins";
import { getMaxClicks as getDealRoomMaxClicks } from "./slides/RoundResultsDealRoomTable";
import { getMaxClicks as getCeoTransferMaxClicks } from "./slides/RoundResultsCeoTransfer";
import { maxClicks as executiveDecisionsTableMaxClicks } from "./slides/RoundResultsExecutiveDecisionsTable";
import { maxClicks as neoBankMaxClicks } from "./slides/RoundResultsNeoBankAmlIssue";
import { maxClicks as royalCommissionClicks } from "./slides/RoundResultsRoyalCommission";
import { getMaxClicks as getRegCapitalMaxClicks } from "./slides/RoundResultsRegulatoryCapitalIntervention";
import { getMaxClicks as getNewsRoomMaxClicks } from "./slides/RoundResultsNewsRoom";
import { useKeypress } from "../../../hooks/useKeypress";
import Icon from "../../atoms/icon/Icon";
import Clickable from "../../atoms/clickable/Clickable";
import useDimensions from "../../../hooks/useDimensions";
import RoundResultsPage from "./RoundResultsPage";
import { addSecondsToDate, formatTime } from "../../../lib/date";
import RoundResultsPresenterSlidePreview from "./components/RoundResultsPresenterSlidePreview";
import {
  formatDuration,
  getTotalDurationEstimate,
} from "../reporting-templates/utils/durationUtils";
import InformationPopup from "../../organisms/information-popup/InformationPopup";
import { ThemeFontSize } from "../../../types/theme";
import RoundResultsFontMultiplierModal from "./components/RoundResultsFontMultiplierModal";
import { useResultsContext } from "./context/context";
import RoundResultsLayout from "./RoundResultsLayout";

const RENDERED_DIMENSIONS = { width: 1700, height: 800 };
const containerPadding = 4;
const headingHeight = 160;
const innerContainerPadding = 40;
const speakerNotesSizes: ThemeFontSize[] = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl",
  "3xl",
];

interface Props {
  data: ModelAPI.Reporting.ReportingResults;
}

const RoundResultsPresenterLayout = ({ data }: Props) => {
  const context = useResultsContext();
  const [speakerNotesSize, setSpeakerNotesSize] = useState<ThemeFontSize>("lg");
  const [textSizeModalOpen, setTextSizeModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const [
    { previewHeight, previewWidth, availableHeight, availableWidth },
    setContainerRef,
  ] = useDimensions(
    headingHeight,
    containerPadding,
    innerContainerPadding,
    RENDERED_DIMENSIONS,
  );

  const [sidebarDimensions, setSidebarRef] = useDimensions(
    1,
    0,
    0,
    RENDERED_DIMENSIONS,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(interval);
  });

  const isSmallestSpeakerNoteSize = useMemo(
    () => speakerNotesSize === speakerNotesSizes[0],
    [speakerNotesSize],
  );
  const isBiggestSpeakerNoteSize = useMemo(
    () => speakerNotesSize === speakerNotesSizes[speakerNotesSizes.length - 1],
    [speakerNotesSize],
  );

  const increaseSpeakerFontSize = useCallback(() => {
    const currentIndex = speakerNotesSizes.indexOf(speakerNotesSize);
    if (!isBiggestSpeakerNoteSize) {
      setSpeakerNotesSize(speakerNotesSizes[currentIndex + 1]);
    }
  }, [speakerNotesSize, isBiggestSpeakerNoteSize]);

  const decreaseSpeakerFontSize = useCallback(() => {
    const currentIndex = speakerNotesSizes.indexOf(speakerNotesSize);
    if (!isSmallestSpeakerNoteSize) {
      setSpeakerNotesSize(speakerNotesSizes[currentIndex - 1]);
    }
  }, [speakerNotesSize, isSmallestSpeakerNoteSize]);

  const selectedLayout = useMemo(() => {
    return data?.layouts[context.pageIndex];
  }, [context.pageIndex, data?.layouts]);

  const nextLayout = useMemo(() => {
    return data?.layouts[context.pageIndex + 1];
  }, [context.pageIndex, data?.layouts]);

  const handleActualClick = useCallback(() => {
    if (!selectedLayout || !data) return;
    let maxClicks = 0;
    if (selectedLayout.type === "bank_bail_outs") {
      maxClicks = getBankBailOutsMaxClicks(data.bankBailouts);
    } else if (selectedLayout.type === "net_interest_margin") {
      maxClicks = marketMargingMaxClicks;
    } else if (selectedLayout.type === "deal_room_results") {
      maxClicks = getDealRoomMaxClicks(data.dealRoom!);
    } else if (selectedLayout.type === "ceo_transfer") {
      maxClicks = getCeoTransferMaxClicks(data.simulationType);
    } else if (selectedLayout.type === "executive_decisions_table") {
      maxClicks = executiveDecisionsTableMaxClicks;
    } else if (selectedLayout.type === "neobank_aml_issue") {
      maxClicks = neoBankMaxClicks;
    } else if (selectedLayout.type === "royal_commission") {
      maxClicks = royalCommissionClicks;
    } else if (selectedLayout.type === "regulatory_capital_intervention") {
      maxClicks = getRegCapitalMaxClicks(data.regulatoryCapitalIntervention);
    } else if (selectedLayout.type === "news_room") {
      maxClicks = getNewsRoomMaxClicks(false);
    }
    if (context.clicks < maxClicks) {
      context.addClick();
    } else {
      context.goNextPage();
    }
  }, [context, data, selectedLayout]);

  const onNextClick = useCallback(() => {
    context.goNextPage();
  }, [context]);

  const onResetClick = useCallback(() => {
    context.updatePageAndClicks({
      pageIndex: 0,
      clicks: 0,
      version: new Date().getTime(),
    });
  }, [context]);

  const onBackClick = useCallback(() => {
    context.goPreviousPage();
  }, [context]);

  const keymap = useMemo(() => {
    return {
      ArrowDown: handleActualClick,
      ArrowUp: onBackClick,
    };
  }, [handleActualClick, onBackClick]);

  useKeypress({ keymap });

  const timeRemaining = useMemo(
    () => getTotalDurationEstimate(data?.layouts ?? [], context.pageIndex),
    [context.pageIndex, data?.layouts],
  );

  return (
    <>
      {textSizeModalOpen && (
        <RoundResultsFontMultiplierModal
          isOpen={textSizeModalOpen}
          onClose={() => setTextSizeModalOpen(false)}
        />
      )}
      {data && (
        <div style={{ height: "100vh", width: "100%" }}>
          <InlineGroup fullHeight block>
            <VerticalGroup
              full
              className="width-70-percent p-2"
              spread
              bgColour="darkgrey"
              ref={setContainerRef}
            >
              <VerticalGroup wide>
                <InlineGroup
                  className="pl-2 pr-2"
                  verticalCenter
                  block
                  evenWidthChildren
                >
                  <InlineGroup verticalCenter>
                    <Text size="lg" colour="white">
                      Remaining Time Estimate: {formatDuration(timeRemaining)} (
                      {formatTime(addSecondsToDate(timeRemaining, currentDate))}
                      )
                    </Text>
                  </InlineGroup>
                  <InlineGroup verticalCenter right>
                    <Text colour="white" size="lg">
                      {formatTime(currentDate)}
                    </Text>
                  </InlineGroup>
                </InlineGroup>
                <VerticalGroup className="p-4" center wide>
                  {previewHeight && (
                    <RoundResultsPresenterSlidePreview
                      availableWidth={availableWidth}
                      availableHeight={availableHeight}
                      previewWidth={previewWidth}
                      previewHeight={previewHeight}
                    >
                      <RoundResultsLayout data={data} />
                      {/* id={"preview"}
                        eventId={context.eventId}
                        data={data}
                        roundNumber={context.roundNumber}
                        onlyThesePages={[]}
                        startingLayout={selectedLayout?.type}
                        hidePagination
                        // syncState
                        // readSyncState
                        // ignoreKeyPress */}
                      {/* /> */}
                    </RoundResultsPresenterSlidePreview>
                  )}
                </VerticalGroup>
              </VerticalGroup>
              <InlineGroup
                className="pl-2 pr-2"
                verticalCenter
                block
                evenWidthChildren
              >
                <InlineGroup verticalCenter spaceBetweenElements={2}>
                  <Clickable onClick={onResetClick}>
                    <Icon
                      type="refresh"
                      colour="white"
                      size={6}
                      tip={{ content: "Restart presentation", id: "restart" }}
                    />
                  </Clickable>
                  <Clickable onClick={() => setTextSizeModalOpen(true)}>
                    <Icon
                      type="textSize"
                      colour="white"
                      size={6}
                      tip={{ content: "Change text size", id: "tezt-size" }}
                    />
                  </Clickable>
                  <Clickable
                    onClick={() =>
                      context.updateCastState({ cast: !context.cast })
                    }
                  >
                    <Icon
                      type="cast"
                      colour={context.cast ? "secondaryBlue" : "white"}
                      size={6}
                      tip={{ content: "Cast to participants", id: "cast" }}
                    />
                  </Clickable>
                </InlineGroup>
                <InlineGroup verticalCenter center>
                  <Clickable
                    onClick={onBackClick}
                    disabled={context.pageIndex === 0}
                  >
                    <Icon
                      type="leftChevron"
                      colour="white"
                      size={10}
                      tip={{ content: "Go to previous slide", id: "previous" }}
                    />
                  </Clickable>
                  <Text colour="white" size="lg">
                    Slide {context.pageIndex + 1} of {data.layouts.length}
                  </Text>
                  <Clickable onClick={onNextClick} disabled={!nextLayout}>
                    <Icon
                      type="rightChevron"
                      colour="white"
                      size={10}
                      tip={{ content: "Go to next slide", id: "next" }}
                    />
                  </Clickable>
                  <InformationPopup
                    title="Controlling the slides"
                    body={
                      <>
                        <p>Use the arrow keys to control the slides:</p>
                        <p>Up arrow: Go back one slide</p>
                        <p>Down arrow: Click through the current slide</p>
                      </>
                    }
                  />
                </InlineGroup>
                <InlineGroup verticalCenter right>
                  <Text size="lg" colour="white">
                    Total Time Estimate:{" "}
                    {formatDuration(getTotalDurationEstimate(data.layouts, 0))}
                  </Text>
                </InlineGroup>
              </InlineGroup>
            </VerticalGroup>
            <VerticalGroup
              full
              className="width-30-percent"
              center
              bgColour="darkgrey"
              borderLeft
              borderColour="secondaryDark4"
              ref={setSidebarRef}
            >
              <VerticalGroup wide center>
                <InlineGroup className="p-2" block verticalCenter center>
                  <Text size="lg" colour="white">
                    Next Slide
                  </Text>
                </InlineGroup>
                <VerticalGroup className="p-4" center wide>
                  {nextLayout && sidebarDimensions.previewHeight && (
                    <RoundResultsPresenterSlidePreview
                      availableWidth={sidebarDimensions.availableWidth}
                      availableHeight={sidebarDimensions.availableHeight}
                      previewWidth={sidebarDimensions.previewWidth}
                      previewHeight={sidebarDimensions.previewHeight}
                      paddingLeft={20}
                      paddingRight={20}
                      heightOffset={20}
                    >
                      <RoundResultsPage
                        id={"sidebar"}
                        eventId={context.eventId}
                        data={data}
                        roundNumber={context.roundNumber}
                        onlyThesePages={[]}
                        startingLayout={nextLayout?.type}
                        hidePagination
                        noAnimations
                        ignoreKeyPress
                      />
                    </RoundResultsPresenterSlidePreview>
                  )}
                  {!nextLayout && (
                    <Text size="lg" colour="white">
                      End of slides
                    </Text>
                  )}
                </VerticalGroup>
              </VerticalGroup>
              <VerticalGroup wide spread full>
                <VerticalGroup
                  className="p-2"
                  wide
                  center
                  spaceBetweenElements={2}
                >
                  <Text size="lg" colour="white">
                    Notes
                  </Text>
                  <Text size={speakerNotesSize} colour="secondaryDark4" isHtml>
                    {selectedLayout?.speakerNotes}
                  </Text>
                </VerticalGroup>
                <InlineGroup className="pl-2 pr-2 pb-4">
                  <Clickable
                    onClick={decreaseSpeakerFontSize}
                    disabled={isSmallestSpeakerNoteSize}
                  >
                    <Icon
                      type="roundMinus"
                      size={6}
                      colour={
                        isSmallestSpeakerNoteSize ? "grey1" : "secondaryBlue"
                      }
                      tip={{
                        content: "Decrease speaker note text size",
                        id: "speaker-minus",
                      }}
                    />
                  </Clickable>
                  <Clickable
                    onClick={increaseSpeakerFontSize}
                    disabled={isBiggestSpeakerNoteSize}
                  >
                    <Icon
                      type="roundPlus"
                      size={6}
                      colour={
                        isBiggestSpeakerNoteSize ? "grey1" : "secondaryBlue"
                      }
                      tip={{
                        content: "Increase speaker note text size",
                        id: "speaker-plus",
                      }}
                    />
                  </Clickable>
                </InlineGroup>
              </VerticalGroup>
            </VerticalGroup>
          </InlineGroup>
        </div>
      )}
    </>
  );
};

export default RoundResultsPresenterLayout;
